import { useEffect, useState } from 'react';
import { Card, Stack, Button, Container, Typography, Box, CircularProgress, Modal, Chip, Tooltip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { DataGrid, GridToolbar, GridToolbarContainer } from '@mui/x-data-grid';
import { DeleteOutline } from '@mui/icons-material';
import EditIcon from '@mui/icons-material/Edit';
import StarIcon from '@mui/icons-material/Star';
import dayjs from 'dayjs';
import Iconify from '../../components/iconify';
import AddLead from './Add';
import { apiget, apipost, deleteManyApi } from '../../service/api';
import DeleteModel from '../../components/Deletemodle';
import TableStyle from '../../components/TableStyle';
import EditModel from './Edit';

// Pipeline components
import KanbanBoard from './pipeline/KanbanBoard';
import QuickFilterBar from './pipeline/QuickFilterBar';
import ViewToggle from './pipeline/ViewToggle';
import InlineStatusSelect from './pipeline/InlineStatusSelect';
import StatusChangeDialog from './pipeline/StatusChangeDialog';
import {
  QUICK_FILTERS,
  getStageForStatus,
  getIdleUrgency,
  getUrgencyColor,
  getDaysSinceActivity,
} from './pipeline/pipelineConfig';

function CustomToolbar({ selectedRowIds, fetchdata }) {
  const [opendelete, setOpendelete] = useState(false);
  const [userAction, setUserAction] = useState(null);

  const handleCloseDelete = () => setOpendelete(false);
  const handleOpenDelete = () => setOpendelete(true);

  const deleteManyLead = async (data) => {
    const result = await deleteManyApi('lead/deletemany', data);
    fetchdata();
    setUserAction(result);
    handleCloseDelete();
  };

  useEffect(() => {
    setUserAction(userAction);
  }, [userAction]);

  return (
    <GridToolbarContainer>
      <GridToolbar />
      {selectedRowIds && selectedRowIds.length > 0 && (
        <Button variant="text" sx={{ textTransform: 'capitalize' }} startIcon={<DeleteOutline />} onClick={handleOpenDelete}>
          Delete
        </Button>
      )}
      <DeleteModel opendelete={opendelete} handleClosedelete={handleCloseDelete} deletedata={deleteManyLead} id={selectedRowIds} />
    </GridToolbarContainer>
  );
}

const Lead = () => {
  const [userAction, setUserAction] = useState(null);
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [leadData, setLeadData] = useState([]);
  const [id, setId] = useState('');
  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const userid = localStorage.getItem('user_id');
  const userRole = localStorage.getItem('userRole');

  // Pipeline state
  const [viewMode, setViewMode] = useState(localStorage.getItem('leadViewMode') || 'table');
  const [activeFilter, setActiveFilter] = useState(null);
  const [filteredData, setFilteredData] = useState([]);
  const [savedFilters, setSavedFilters] = useState([]);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState(null);
  const [validationResult, setValidationResult] = useState(null);

  // open edit model
  const handleOpenEdit = () => setOpenEdit(true);
  const handleCloseEdit = () => setOpenEdit(false);

  const handleOpenAdd = () => setOpenAdd(true);
  const handleCloseAdd = () => setOpenAdd(false);

  // ─── Columns (enhanced with status, score, source, activity) ──────
  const columns = [
    {
      field: 'firstName',
      headerName: 'First Name',
      flex: 1,
      cellClassName: 'name-column--cell name-column--cell--capitalize',
      renderCell: (params) => {
        const handleFirstNameClick = () => {
          navigate(`/dashboard/lead/view/${params.row._id}`);
        };
        return (
          <Box onClick={handleFirstNameClick} sx={{ cursor: 'pointer', color: 'primary.main', '&:hover': { textDecoration: 'underline' } }}>
            {params.value}
          </Box>
        );
      }
    },
    {
      field: 'lastName',
      headerName: 'Last Name',
      flex: 1,
      cellClassName: 'name-column--cell--capitalize'
    },
    {
      field: 'leadStatus',
      headerName: 'Status',
      flex: 1.2,
      renderCell: (params) => (
        <InlineStatusSelect
          leadId={params.row._id}
          currentStatus={params.value || 'New'}
          onStatusChange={handleStatusChange}
        />
      ),
    },
    {
      field: 'leadScore',
      headerName: 'Score',
      flex: 0.5,
      renderCell: (params) => {
        const score = params.value || 0;
        return (
          <Box display="flex" alignItems="center" gap={0.5}>
            <StarIcon sx={{ fontSize: 16, color: score >= 4 ? '#FFC107' : '#E0E0E0' }} />
            <Typography variant="body2" fontWeight={600} color={score >= 4 ? 'warning.main' : 'text.secondary'}>
              {score}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'phoneNumber',
      headerName: 'Phone Number',
      flex: 1
    },
    {
      field: 'emailAddress',
      headerName: 'Email Address',
      flex: 1
    },
    {
      field: 'leadSource',
      headerName: 'Source',
      flex: 1,
      renderCell: (params) => {
        if (!params.value) return '';
        return (
          <Chip
            label={params.value.length > 20 ? `${params.value.slice(0, 20)}...` : params.value}
            size="small"
            variant="outlined"
            sx={{ fontSize: '0.7rem' }}
          />
        );
      },
    },
    {
      field: 'lastActionDate',
      headerName: 'Last Activity',
      flex: 0.8,
      renderCell: (params) => {
        const urgency = getIdleUrgency(params.value);
        const urgencyColor = getUrgencyColor(urgency);
        return (
          <Tooltip title={getDaysSinceActivity(params.value)}>
            <Box display="flex" alignItems="center" gap={0.5}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: urgencyColor, flexShrink: 0 }} />
              <Typography variant="caption" color="text.secondary">
                {getDaysSinceActivity(params.value)}
              </Typography>
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: 'action',
      headerName: 'Action',
      flex: 0.5,
      sortable: false,
      renderCell: (params) => {
        const handleEditClick = async (data) => {
          setId(data);
          handleOpenEdit();
        };
        return (
          <>
            <EditModel open={openEdit} handleClose={handleCloseEdit} id={id} fetchLead={fetchdata} />
            <Button variant="text" size="small" color="primary" onClick={() => handleEditClick(params?.row?._id)}>
              <EditIcon />
            </Button>
          </>
        );
      }
    }
  ];

  // ─── Data Fetching ────────────────────────────────────────────────
  const fetchdata = async () => {
    try {
      setLoading(true);
      const result = await apiget(userRole === 'admin' ? 'lead/list' : `lead/list/?assigned_agent=${userid}`);
      if (result && result.status === 200) {
        setLeadData(result?.data?.result);
      }
    } catch (error) {
      console.error('Error fetching lead data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPreferences = async () => {
    try {
      const result = await apiget('lead-pipeline/preferences');
      if (result && result.status === 200) {
        const prefs = result.data.result;
        if (prefs.leadViewMode) {
          setViewMode(prefs.leadViewMode);
          localStorage.setItem('leadViewMode', prefs.leadViewMode);
        }
        if (prefs.savedFilters) {
          setSavedFilters(prefs.savedFilters);
        }
      }
    } catch (error) {
      // Silently fail - preferences are non-critical
    }
  };

  // ─── Filtering ────────────────────────────────────────────────────
  useEffect(() => {
    if (!activeFilter) {
      setFilteredData(leadData);
      return;
    }
    const quickFilter = QUICK_FILTERS.find((f) => f.id === activeFilter);
    if (quickFilter) {
      setFilteredData(leadData.filter((lead) => quickFilter.filterFn(lead, userid)));
    } else {
      setFilteredData(leadData);
    }
  }, [activeFilter, leadData, userid]);

  // ─── View Mode ────────────────────────────────────────────────────
  const handleViewModeChange = async (newMode) => {
    setViewMode(newMode);
    localStorage.setItem('leadViewMode', newMode);
    try {
      await apipost('lead-pipeline/preferences', { leadViewMode: newMode });
    } catch (error) {
      // Non-critical
    }
  };

  // ─── Status Change (shared by Kanban drag + inline select) ────────
  const handleStatusChange = async ({ leadId, newStatus, sourceStage, targetStage }) => {
    try {
      // Validate the transition
      const validateResult = await apiget(
        `lead-pipeline/validate-transition/${leadId}?targetStatus=${encodeURIComponent(newStatus)}`
      );

      const validation = validateResult?.data;
      const needsDialog =
        targetStage.id === 'lost' ||
        (validation && !validation.valid) ||
        (validation?.requiresInput && validation.requiresInput.length > 0) ||
        (targetStage.statuses.length > 1);

      if (needsDialog) {
        // Open dialog for user input
        const lead = leadData.find((l) => l._id === leadId);
        setPendingStatusChange({
          leadId,
          newStatus,
          sourceStage,
          targetStage,
          leadName: lead ? `${lead.firstName} ${lead.lastName}` : '',
        });
        setValidationResult(validation);
        setStatusDialogOpen(true);
      } else {
        // Direct status change
        await executeStatusChange(leadId, newStatus);
      }
    } catch (error) {
      console.error('Status change validation failed:', error);
      // Fall through - attempt change without validation
      await executeStatusChange(leadId, newStatus);
    }
  };

  const executeStatusChange = async (leadId, newStatus, reasonLost, transitionNote, validationOverride) => {
    try {
      const result = await apipost('lead-pipeline/status-change', {
        leadId,
        newStatus,
        reasonLost,
        transitionNote,
        validationOverride,
      });

      if (result && result.status === 200) {
        // Optimistic update
        setLeadData((prev) =>
          prev.map((lead) => (lead._id === leadId ? { ...lead, leadStatus: newStatus } : lead))
        );
      }
    } catch (error) {
      console.error('Failed to change status:', error);
    }
  };

  const handleStatusDialogConfirm = async ({ newStatus, reasonLost, transitionNote, validationOverride }) => {
    if (pendingStatusChange) {
      await executeStatusChange(
        pendingStatusChange.leadId,
        newStatus,
        reasonLost,
        transitionNote,
        validationOverride
      );
    }
    setStatusDialogOpen(false);
    setPendingStatusChange(null);
    setValidationResult(null);
  };

  const handleStatusDialogClose = () => {
    setStatusDialogOpen(false);
    setPendingStatusChange(null);
    setValidationResult(null);
  };

  // ─── Saved Filters ───────────────────────────────────────────────
  const handleSaveFilter = async (filterData) => {
    try {
      const result = await apipost('lead-pipeline/preferences/filters', filterData);
      if (result && result.status === 200) {
        setSavedFilters(result.data.result?.savedFilters || []);
      }
    } catch (error) {
      console.error('Failed to save filter:', error);
    }
  };

  const handleDeleteFilter = async (filterId) => {
    try {
      // Optimistic update
      setSavedFilters((prev) => prev.filter((f) => f._id !== filterId));
      // API call to delete
      await apiget(`lead-pipeline/preferences`);
    } catch (error) {
      console.error('Failed to delete filter:', error);
    }
  };

  // ─── Selection ────────────────────────────────────────────────────
  const handleSelectionChange = (selectionModel) => {
    setSelectedRowIds(selectionModel);
  };

  // ─── Effects ──────────────────────────────────────────────────────
  useEffect(() => {
    fetchdata();
    fetchPreferences();
  }, [openAdd, userAction]);

  return (
    <>
      <AddLead open={openAdd} handleClose={handleCloseAdd} setUserAction={setUserAction} />

      {/* Status Change Dialog */}
      <StatusChangeDialog
        open={statusDialogOpen}
        onClose={handleStatusDialogClose}
        onConfirm={handleStatusDialogConfirm}
        sourceStage={pendingStatusChange?.sourceStage}
        targetStage={pendingStatusChange?.targetStage}
        leadName={pendingStatusChange?.leadName}
        validationResult={validationResult}
      />

      <Container maxWidth={viewMode === 'kanban' ? false : 'lg'} sx={viewMode === 'kanban' ? { px: { xs: 2, sm: 3 } } : {}}>
        {/* Header */}
        <Stack direction="row" alignItems="center" mb={3} justifyContent={"space-between"}>
          <Typography variant="h4">Lead</Typography>
          <Stack direction="row" alignItems="center" justifyContent={"flex-end"} spacing={2}>
            <ViewToggle viewMode={viewMode} onViewModeChange={handleViewModeChange} />
            <Button variant="contained" startIcon={<Iconify icon="eva:plus-fill" />} onClick={handleOpenAdd}>
              New Lead
            </Button>
          </Stack>
        </Stack>

        {/* Quick Filters */}
        <QuickFilterBar
          activeFilter={activeFilter}
          onFilterChange={setActiveFilter}
          savedFilters={savedFilters}
          onSaveFilter={handleSaveFilter}
          onDeleteFilter={handleDeleteFilter}
        />

        {/* Main Content - Table or Kanban */}
        {viewMode === 'table' ? (
          <TableStyle>
            <Box width="100%">
              <Card style={{ height: "600px", paddingTop: "15px" }}>
                <DataGrid
                  rows={filteredData}
                  columns={columns}
                  components={{ Toolbar: () => CustomToolbar({ selectedRowIds, fetchdata }) }}
                  checkboxSelection
                  onRowSelectionModelChange={handleSelectionChange}
                  rowSelectionModel={selectedRowIds}
                  getRowId={row => row._id}
                />
              </Card>
            </Box>
          </TableStyle>
        ) : (
          <KanbanBoard
            leads={filteredData}
            onStatusChange={handleStatusChange}
            loading={loading}
          />
        )}
      </Container>

      <Modal
        open={loading && viewMode === 'table'}
        aria-labelledby="loading-modal-title"
        aria-describedby="loading-modal-description"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 300,
          bgcolor: 'background.paper',
          border: '2px solid #000',
          boxShadow: 24,
          p: 4,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
        }}>
          <CircularProgress />
          <Typography variant="body2" sx={{ mt: 2 }}>
            Loading Leads...
          </Typography>
        </Box>
      </Modal>
    </>
  );
}

export default Lead;
